import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Seo from '../components/common/Seo';
import NotFound from './NotFound';
import { formatPrice, getDish } from '../data/dishes';
import { buildDishSchemas } from '../data/dishSchema';
import { addressLine, business, hoursDisplay } from '../data/business';

const easing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const DishPage = () => {
  const { slug } = useParams();
  const dish = getDish(slug);

  // Unknown slug: render the 404 page, which is noindex. Redirecting to the
  // hub instead would tell Google the bogus URL is a valid alias for a real
  // page — a soft 404, and worse than simply not existing.
  if (!dish) return <NotFound />;

  const path = `/dishes/${dish.slug}`;
  const related = dish.related.map(getDish).filter(Boolean);

  const [menuItemSchema, breadcrumbSchema, faqSchema] = buildDishSchemas(dish);

  return (
    <>
      <Seo
        title={dish.title}
        description={dish.metaDescription}
        path={path}
        schemas={[menuItemSchema, breadcrumbSchema, faqSchema]}
      />

      <Layout>
        {/* ── Hero ── */}
        <section className="relative bg-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/images/Hero Background Photos/optimized/IMG_8254.webp"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" />
          </div>

          <div className="relative z-10 container mx-auto px-6 lg:px-8 py-20 md:py-28">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-body tracking-widest uppercase text-white/60">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li><Link to="/dishes" className="hover:text-primary transition-colors">Signature Dishes</Link></li>
                <li aria-hidden="true">/</li>
                <li className="text-primary">{dish.name}</li>
              </ol>
            </nav>

            <motion.p
              className="text-sm tracking-widest uppercase text-primary/90 font-medium font-body mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easing }}
            >
              {dish.menuSection} · {dish.thaiScript} {dish.romanization}
            </motion.p>

            <motion.h1
              className="font-heading text-4xl sm:text-5xl md:text-6xl text-white leading-tight max-w-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.12, ease: easing }}
            >
              {dish.h1}
            </motion.h1>

            <motion.p
              className="font-body text-lg text-white/85 max-w-2xl mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            >
              {dish.menuDescription}
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4 mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
            >
              <a
                href={business.orderUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-track="order_click"
                className="btn-primary rounded-sm"
              >
                Order {dish.name} Online →
              </a>
              <a
                href={business.telephoneHref}
                data-track="call_click"
                className="px-8 py-3 border border-white/50 text-white font-body font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                Call {business.telephoneDisplay}
              </a>
            </motion.div>
          </div>
        </section>

        {/* ── Body ── */}
        <div className="bg-background bg-paper-texture">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="max-w-3xl">

              {/* About the dish */}
              <section>
                <p className="text-sm tracking-widest uppercase text-primary-ink font-medium font-body mb-4">
                  About the dish
                </p>
                <h2 className="font-heading text-3xl md:text-4xl text-secondary">
                  What {dish.name.toLowerCase()} is, and how we make it
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />
                {dish.intro.map((para) => (
                  <p key={para.slice(0, 24)} className="font-body text-text/90 leading-relaxed mt-6">
                    {para}
                  </p>
                ))}
              </section>

              {/* Carries the "best [dish] in Seattle" phrasing as a question,
                  not as a claim about ourselves. */}
              <section className="mt-14">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                  {dish.bestSection.heading}
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />
                <p className="font-body text-text/90 leading-relaxed mt-6">
                  {dish.bestSection.body}
                </p>
              </section>

              {/* On the menu */}
              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                  On the Thaprachan menu
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />

                <div className="mt-6 bg-white rounded-sm shadow-elegant p-6 md:p-8">
                  <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-neutral-warm pb-4">
                    <h3 className="font-heading text-xl text-secondary">
                      {dish.menuName}
                      {dish.menuName !== dish.name && (
                        <span className="font-body text-sm text-text/60"> · {dish.name}</span>
                      )}
                    </h3>
                    <p className="font-body font-semibold text-primary-ink tabular-nums">
                      {formatPrice(dish.basePrice)}
                    </p>
                  </div>

                  <p className="font-body text-text/85 leading-relaxed mt-4">
                    {dish.menuDescription}
                  </p>

                  <h4 className="font-body text-xs font-semibold tracking-widest uppercase text-text/60 mt-6 mb-3">
                    What&rsquo;s in it
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 font-body text-sm text-text/85">
                    {dish.ingredients.map((ing) => (
                      <li key={ing} className="flex gap-2">
                        <span className="text-primary" aria-hidden="true">·</span>
                        {ing}
                      </li>
                    ))}
                  </ul>

                  {dish.proteins && (
                    <>
                      <h4 className="font-body text-xs font-semibold tracking-widest uppercase text-text/60 mt-8 mb-3">
                        Choose your protein
                      </h4>
                      <ul className="font-body text-sm text-text/85 divide-y divide-neutral-warm">
                        {dish.proteins.map((p) => (
                          <li key={p.label} className="flex items-baseline justify-between py-2">
                            <span>{p.label}</span>
                            <span className="tabular-nums text-text/70">
                              {p.extraCost === 0 ? 'included' : `+${formatPrice(p.extraCost)}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  <p className="font-body text-sm text-text/70 mt-6">
                    Prices as listed on our{' '}
                    <Link to="/menu" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                      full menu
                    </Link>
                    .
                  </p>
                </div>
              </section>

              {/* FAQ */}
              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                  {dish.name} questions
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />

                <dl className="mt-6 divide-y divide-neutral-warm border-t border-neutral-warm">
                  {dish.faqs.map((faq) => (
                    <div key={faq.question} className="py-5">
                      <dt className="font-heading text-lg text-secondary mb-2">{faq.question}</dt>
                      <dd className="font-body text-text/85 leading-relaxed">{faq.answer}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              {/* Where to find it */}
              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                  Where to order {dish.name.toLowerCase()} in Seattle
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />

                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div className="bg-white rounded-sm shadow-elegant p-6">
                    <h3 className="font-heading text-lg text-secondary mb-3">Find us</h3>
                    <address className="not-italic font-body text-sm text-text/85 leading-relaxed">
                      {addressLine}
                      <br />
                      <a href={business.telephoneHref} data-track="call_click" className="text-primary-ink hover:text-secondary">
                        {business.telephoneDisplay}
                      </a>
                    </address>
                    <p className="font-body text-sm text-text/70 mt-3">
                      About a 10-minute drive from the{' '}
                      <Link to="/near-uw" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                        University of Washington
                      </Link>
                      .
                    </p>
                    <a
                      href={business.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-4 font-body text-sm text-primary-ink underline underline-offset-2 hover:text-secondary"
                    >
                      Get directions →
                    </a>
                  </div>

                  <div className="bg-white rounded-sm shadow-elegant p-6">
                    <h3 className="font-heading text-lg text-secondary mb-3">Hours</h3>
                    <ul className="font-body text-sm text-text/85 space-y-1">
                      {hoursDisplay.map((h) => (
                        <li key={h.day} className="flex justify-between gap-4">
                          <span>{h.day}</span>
                          <span className="text-text/70 tabular-nums">{h.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              {/* Related dishes */}
              {related.length > 0 && (
                <section className="mt-16">
                  <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                    Also on the menu
                  </h2>
                  <div className="mt-3 h-0.5 w-12 bg-primary" />

                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    {related.map((r) => (
                      <Link
                        key={r!.slug}
                        to={`/dishes/${r!.slug}`}
                        className="block bg-white rounded-sm shadow-elegant p-5 hover:shadow-premium transition-shadow"
                      >
                        <h3 className="font-heading text-lg text-secondary mb-1">{r!.name}</h3>
                        <p className="font-body text-sm text-text/70 line-clamp-3">
                          {r!.menuDescription}
                        </p>
                        <span className="inline-block mt-3 font-body text-xs tracking-widest uppercase text-primary-ink">
                          {formatPrice(r!.basePrice)} →
                        </span>
                      </Link>
                    ))}
                  </div>

                  <p className="font-body text-sm text-text/70 mt-6">
                    See all five{' '}
                    <Link to="/dishes" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                      signature dishes
                    </Link>{' '}
                    or browse the{' '}
                    <Link to="/menu" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                      full Thaprachan menu
                    </Link>
                    .
                  </p>
                </section>
              )}

            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default DishPage;
